import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Calendar, MapPin, Users, Car, Shirt, Ticket as TicketIcon, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { formatEventDate } from "@/lib/utils";
import SmartImage from "@/components/SmartImage";

export default function EventDetails() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/events/${id}`).then(r => setEvent(r.data)).catch(() => nav("/events")); }, [id, nav]);

  const buy = async () => {
    if (!user) { toast.info("Please log in to buy tickets"); nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { event_id: id, quantity: qty, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const waitlist = async () => {
    if (!user) { toast.info("Please log in to join the waitlist"); nav("/login"); return; }
    try { await api.post(`/events/${id}/waitlist`); toast.success("You're on the waitlist"); } catch { toast.error("Something went wrong"); }
  };

  if (!event) return <div className="max-w-6xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;
  const dateStr = formatEventDate(event.date, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const isFull = event.is_full ?? (event.spots_remaining === 0);
  const registrationOpen = event.registration_open !== false;
  const registrationClosed = !registrationOpen || isFull;
  const allowWaitlist = event.allow_waitlist_when_full !== false;
  const showWaitlist = isFull && allowWaitlist;

  return (
    <div data-testid="event-details-page">
      <div className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <SmartImage
          src={event.image_url}
          alt={event.title}
          className="w-full h-full"
          style={{ objectPosition: `${event.image_focal_x ?? 50}% ${event.image_focal_y ?? 50}%` }}
          fetchPriority="high"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-6 flex flex-col justify-end pb-12">
          <div className="text-eyebrow text-[hsl(46_65%_75%)]">Curated event</div>
          <h1 className="font-heading text-4xl md:text-6xl text-white mt-3 leading-tight max-w-3xl" data-testid="event-title">{event.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <div className="text-eyebrow">About</div>
            <p className="text-neutral-700 leading-relaxed mt-3 text-lg">{event.description}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <InfoRow icon={Calendar} label="Date & time" value={`${dateStr} · ${event.time}`} />
            <InfoRow icon={MapPin} label="Venue" value={<>{event.venue}<br/><span className="text-neutral-500 text-sm">{event.address}</span></>} />
            <InfoRow icon={Users} label="Ages" value={`${event.age_min} – ${event.age_max}`} />
            <InfoRow icon={Shirt} label="Dress code" value={event.dress_code} />
            <InfoRow icon={Car} label="Parking" value={event.parking_info || "See venue notes"} />
            {isFull && (
              <InfoRow icon={TicketIcon} label="Availability" value="Sold out" />
            )}
          </div>

          {event.schedule?.length > 0 && (
            <div>
              <div className="text-eyebrow">Schedule</div>
              <ul className="mt-4 space-y-3">
                {event.schedule.map((s, i) => (
                  <li key={`${s.time}-${s.item}`} className="flex gap-6 items-baseline border-b border-neutral-200 pb-3">
                    <span className="font-heading text-lg gold min-w-[80px]">{s.time}</span>
                    <span className="text-neutral-700">{s.item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {event.faq?.length > 0 && (
            <div>
              <div className="text-eyebrow">FAQ</div>
              <Accordion type="single" collapsible className="mt-4">
                {event.faq.map((f, i) => (
                  <AccordionItem value={`f${i}`} key={f.q}>
                    <AccordionTrigger className="font-heading text-lg">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="card-elegant p-8 sticky top-24" data-testid="ticket-purchase-card">
            <div className="font-heading text-4xl">${(event.price_cents/100).toFixed(0)}</div>
            <div className="text-eyebrow mt-1">per ticket</div>
            <div className="divider-gold my-6" />
            {registrationClosed ? (
              <>
                {showWaitlist ? (
                  <>
                    <p className="text-neutral-600 mb-4">This event is sold out. Join the waitlist and we'll notify you if a spot opens.</p>
                    <Button onClick={waitlist} className="w-full rounded-full bg-black text-white hover:bg-neutral-800" data-testid="join-waitlist-btn">Join waitlist</Button>
                  </>
                ) : (
                  <>
                    <p className="text-neutral-600 mb-4">
                      {isFull ? "This event is sold out." : "Registration is currently closed for this event."}
                    </p>
                    <Button disabled className="w-full rounded-full bg-neutral-200 text-neutral-500" data-testid="registration-closed-btn">
                      {isFull ? "Sold out" : "Registration closed"}
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <label className="text-eyebrow block mb-2">Quantity</label>
                <Input type="number" min={1} max={Math.min(4, event.spots_remaining)} value={qty} onChange={(e) => setQty(Math.max(1, +e.target.value || 1))} className="rounded-xl mb-6" data-testid="ticket-quantity-input" />
                <Button disabled={busy} onClick={buy} className="w-full rounded-full bg-black text-white hover:bg-neutral-800 btn-pill" data-testid="buy-ticket-btn">
                  {busy ? "Redirecting…" : `Purchase ticket · $${((event.price_cents * qty)/100).toFixed(0)}`}
                </Button>
                <p className="text-xs text-neutral-500 mt-4 text-center">Secure checkout powered by Stripe</p>
                <Link
                  to="/safety"
                  className="mt-4 flex gap-2 items-start rounded-xl bg-blush/40 border border-blush/60 px-3 py-2.5 text-xs text-neutral-700 hover:bg-blush/60 transition"
                  data-testid="safety-notice-checkout"
                >
                  <ShieldCheck size={14} className="gold shrink-0 mt-0.5" strokeWidth={1.5} />
                  <span>
                    <strong>Register with your legal name.</strong> A valid photo ID matching your registration is required at the door. <span className="underline underline-offset-2">Read our safety commitment →</span>
                  </span>
                </Link>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex gap-4">
    <Icon size={20} strokeWidth={1.5} className="gold mt-1 shrink-0" />
    <div>
      <div className="text-eyebrow">{label}</div>
      <div className="text-neutral-800 mt-1">{value}</div>
    </div>
  </div>
);
